import { ReadingImage } from '@kalos/kalos-rpc';
import { Button, DataTablePagination, Skeleton, toast, useConfirm } from '@kalos/ui';
import { TrashIcon, UploadIcon } from '@radix-ui/react-icons';
import { useState } from 'react';

import {
  useReadingImageCreateMutation,
  useReadingImageDeleteMutation,
} from '../../../../../hooks/react-query/useReadingQuery';
import { useS3FileObjectUrlQUery } from '../../../../../hooks/react-query/useS3';
import { compressImageFileForUpload } from '../../../../../tools/fileUploadUtils';
import { RotateZoomImage } from '../../../../AltGallery/main';
import { MultipleFilesForm } from './MultipleFilesForm';

interface ReadingImagesGalleryProps {
  readingImages: ReadingImage[];
  readingId: number;
  fullScreenEnabled?: boolean;
  showUploadControls?: boolean;
}

export const ReadingImagesGallery = ({
  readingImages,
  readingId,
  fullScreenEnabled = true,
  showUploadControls = true,
}: ReadingImagesGalleryProps) => {
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const isImageQueryEnabled = !!readingImages.at(activeImageIdx);

  const imageQuery = useS3FileObjectUrlQUery({
    filter: {
      bucket: readingImages[activeImageIdx]?.fileBucket,
      fileName: readingImages[activeImageIdx]?.fileName,
      mimeType: readingImages[activeImageIdx]?.mimeType,
    },
    enabled: isImageQueryEnabled,
  });

  const createReadingImageMutation = useReadingImageCreateMutation();
  const deleteReadingImageMutation = useReadingImageDeleteMutation();

  const onImagesAdd: React.ComponentProps<typeof MultipleFilesForm>['onSubmit'] = async ({
    images,
  }) => {
    const results = await Promise.allSettled(
      images.map(async (image) =>
        createReadingImageMutation.mutateAsync({
          file: await compressImageFileForUpload(image),
          req: ReadingImage.create({ readingId }),
        }),
      ),
    );
    const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (rejected.length > 0) {
      rejected.forEach((r) => console.error('Failed to upload reading photo:', r.reason));
      toast({
        title: 'Photo upload failed',
        description: `${rejected.length} of ${images.length} photo(s) did not upload. Please add them again.`,
        variant: 'destructive',
      });
      return;
    }
    setIsAdding(false);
  };

  const confirm = useConfirm();

  const [isDeleting, setIsDeleting] = useState(false);
  const onImageDelete = async () => {
    const readingImage = readingImages.at(activeImageIdx);
    if (!readingImage) return;
    if (!(await confirm({ title: 'Are you sure you want to delete this image?' }))) return;
    try {
      setIsDeleting(true);
      await deleteReadingImageMutation.mutateAsync(readingImage);
      toast({
        title: 'Image Deleted',
        variant: 'success',
      });
      setActiveImageIdx(0);
    } catch {
      toast({ title: 'Failed to delete image', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const preview = !isImageQueryEnabled ? (
    <span>No Images</span>
  ) : (
    <div className="mx-auto mt-4 w-full">
      <div className="flex flex-col gap-4">
        <div className="h-[calc(100vh-320px)] min-h-[400px]">
          {imageQuery.isSuccess ? (
            <RotateZoomImage
              url={imageQuery.data}
              fullScreenEnabled={fullScreenEnabled}
              downloadFilename={readingImages[activeImageIdx]?.fileName || 'reading-image.png'}
            />
          ) : (
            <Skeleton className="mx-auto h-full w-full max-w-[700px]" />
          )}
        </div>
        {/* Pagination sits under the image so the photo itself is the first thing in view. */}
        <DataTablePagination
          className="flex-1"
          currentPage={activeImageIdx}
          pageCount={readingImages.length}
          setPage={setActiveImageIdx}
          disabled={deleteReadingImageMutation.isPending || createReadingImageMutation.isPending}
        />
      </div>
    </div>
  );

  return (
    <div className="flex w-full max-w-[800px] flex-col gap-2">
      {showUploadControls && isAdding && (
        <div className="flex justify-center gap-2">
          <MultipleFilesForm
            onSubmit={onImagesAdd}
            disabled={createReadingImageMutation.isPending}
            isLoading={createReadingImageMutation.isPending}
            actions={
              <Button variant="secondary" onClick={() => setIsAdding(false)}>
                Cancel{' '}
              </Button>
            }
          />
        </div>
      )}

      {showUploadControls && !isAdding && (
        <div className="flex gap-2">
          <Button onClick={() => setIsAdding(true)}>
            <UploadIcon />
            <span>Add Image</span>
          </Button>
          <Button
            isLoading={isDeleting}
            disabled={isDeleting || readingImages.length === 0}
            onClick={onImageDelete}
          >
            <TrashIcon />
            <span>Delete</span>
          </Button>
        </div>
      )}

      {preview}
    </div>
  );
};
